import { ReactNode } from 'react'
import { Navigate, useParams as useV6Params } from 'react-router'

import { setReactHashRouteParams, setReactRouteParams } from '../../utils/routeParams'

export interface RouteElementProps {
  children: ReactNode
  isHashRouter?: boolean
}

/*
 * Params bridge for use as the `element` of a genuine v6 <Route> (e.g. an app-shell <Routes>).
 */
export const RouteElement = ({ children, isHashRouter = false }: RouteElementProps) => {
  const params = useV6Params()

  if (isHashRouter) {
    setReactHashRouteParams(params)
  } else {
    setReactRouteParams(params)
  }

  return <>{children}</>
}

export interface HashIndexRedirectProps {
  // Captured at module mount - by the time this renders, window.location may have moved on.
  modulePathname: string
  to: string
}

/*
 * Drops a hash-routed module's index redirect once the browser Back button has carried us out of
 * that module.
 *
 * Leaving /org/settings#/organization with Back goes: the URL becomes /org/dashboard, pathname
 * changed and hash cleared in one step; Settings is still mounted, so its router sees the empty
 * hash and matches this index route; the redirect asks history for "#/organization"; history
 * writes hashes as relative URLs, so that resolves against /org/dashboard and lands on
 * /org/dashboard#/organization, which the view container turns into a route that does not exist.
 *
 * So before redirecting we check the current window.location.pathname against the one the module
 * mounted under. If they match we are still inside the hash module, so we
 * run it. If they differ, Back has already moved us to another page: no hash
 * gets written, and the URL stays as Back left it.
 */
export const HashIndexRedirect = ({ modulePathname, to }: HashIndexRedirectProps) => {
  if (window.location.pathname !== modulePathname) {
    return null
  }

  return <Navigate to={to} replace />
}
